import { ReactElement } from 'react';
import { IFragmentIFrameCtaFields } from 'types/contentful';
import { BASE_CLASSES } from 'lib/consts';
import { useFactua } from 'context/FactuaContext';

interface IFrameCTAProps extends Omit<IFragmentIFrameCtaFields, 'title'> {
  classNames?: string;
  backgroundColor?: string;
  onClick?: () => void;
  replaceBaseClasses?: string[];
}

interface ColorOptions {
  [key: string]: {
    fill: string;
    text?: string;
    borderColor?: string;
  };
}

const OPTS: ColorOptions = {
  Primary: {
    fill: 'highlight',
    text: 'primary-actual',
  },
  Primary2: {
    fill: 'secondary',
    text: 'black',
  },
  Primary3: {
    fill: 'tertiary',
    text: 'white',
  },
  Outlined: {
    fill: 'transparent',
  },
  OutlinedTeal: {
    fill: 'highlight',
    text: 'primary-actual',
    borderColor: 'border-teal',
  },
  OutlinedDarkteal: {
    fill: 'secondary',
    text: 'black',
    borderColor: 'border-darkteal',
  },
  OutlinedYellow: {
    fill: 'tertiary',
    text: 'black',
    borderColor: 'border-yellow',
  },
  White: {
    fill: 'white',
    text: 'black',
  },
};

const classesHelper = (colorScheme: string, backgroundColor?: string): string => {
  const scheme = OPTS[colorScheme] || OPTS.Primary;

  if (colorScheme === 'Outlined') {
    // white is a special case because it is a color and not a brand concept like muted or primary
    // we therefore need to change the text to black when the background is white and not rely on
    // a text color established by the brand
    const textColor = backgroundColor === 'white' ? 'black' : backgroundColor;
    return `border border-2px border-current bg-${scheme.fill} text-${textColor}`;
  } else if (
    colorScheme === 'OutlinedTeal' ||
    colorScheme === 'OutlinedDarkteal' ||
    colorScheme === 'OutlinedYellow'
  ) {
    return `border border-2px ${scheme.borderColor} text-${scheme.text}`;
  } else {
    return `bg-${scheme.fill} text-${scheme.text}`;
  }
};

// IFrameCTA: Open the slug in an iFrame popup instead of navigating the user to the slug url
export default function IFrameCTA({
  text,
  slug,
  colorScheme,
  backgroundColor,
  classNames = '',
  onClick,
  replaceBaseClasses = [],
}: IFrameCTAProps): ReactElement {
  const classes = classesHelper(colorScheme, backgroundColor);

  const baseClasses = replaceBaseClasses.reduce(
    (classes, replaceClass) => classes.replace(replaceClass, ''),
    BASE_CLASSES
  );

  const { openFactua } = useFactua();

  const handleClick = onClick ? onClick : () => openFactua(slug);

  return (
    <button onClick={handleClick} className={`${baseClasses} ${classes} ${classNames}`}>
      {text}
    </button>
  );
}
